import React from 'react';

import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

import { CHECKLIST_STATUS_STYLES } from '../constants';
import type { ManualApprovalState } from '../types';
import type { EgsMarginChecklistRow } from '../utils';

import ChecklistStatusDot from './ChecklistStatusDot';
import EmptyTableRow from './EmptyTableRow';
import HiddenApprovedFooter from './HiddenApprovedFooter';
import NumberField from './NumberField';
import ReadOnlyField from './ReadOnlyField';
import ReportCard from './ReportCard';
import ReportNote from './ReportNote';
import SortTableCell from './SortTableCell';

interface Props {
  hiddenApprovedCount: number;
  manualApprovals: ManualApprovalState;
  rows: Array<EgsMarginChecklistRow>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onValueChange: (rowId: string, fieldName: 'costPrice' | 'sellPrice', value?: number) => void;
}

export default function MarginReportCard({
  hiddenApprovedCount,
  manualApprovals,
  rows,
  showAutoApproved,
  onApprovalChange,
  onValueChange,
}: Props) {
  return (
    <ReportCard>
      <ReportNote
        icon={<InfoOutlinedIcon />}
        text={
          <>
            <strong>Extra Guest Surcharges margin check:</strong> red is <strong>10% or below</strong>, yellow is{' '}
            <strong>above 10% up to 18%</strong>, and green is <strong>19%+</strong>. Use <strong>Approve</strong> to
            manually approve red/yellow rows.
          </>
        }
      />
      <TableContainer>
        <Table size="small" sx={{ minWidth: 1180 }}>
          <TableHead>
            <TableRow>
              <SortTableCell>Room / Opportunity</SortTableCell>
              <SortTableCell>Extra Guest Type</SortTableCell>
              <SortTableCell>Age Range</SortTableCell>
              <SortTableCell>Cost Price</SortTableCell>
              <SortTableCell>Sell Price</SortTableCell>
              <SortTableCell>% Margin</SortTableCell>
              <SortTableCell>Margin Check</SortTableCell>
              <SortTableCell>Bedding Type</SortTableCell>
              <SortTableCell>Inclusions</SortTableCell>
              <SortTableCell>Collected By</SortTableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <EmptyTableRow colSpan={11} label="No EGS rows match this view." />
            ) : (
              rows.map((row) => {
                const manuallyApproved = !!manualApprovals[row.id];
                const approved = row.status === 'approved' || manuallyApproved;

                return (
                  <TableRow key={row.id} hover sx={approved ? CHECKLIST_STATUS_STYLES.approvedRow : undefined}>
                    <TableCell sx={{ minWidth: 260 }}>
                      <ReadOnlyField value={row.name} />
                      <ReadOnlyField subdued value={row.opportunityName || 'Opportunity not mapped'} />
                    </TableCell>
                    <TableCell>
                      <ReadOnlyField value="N/A" />
                    </TableCell>
                    <TableCell>
                      <ReadOnlyField value="N/A" />
                    </TableCell>
                    <TableCell>
                      <NumberField
                        ariaLabel={`${row.name} cost price`}
                        value={row.costPrice}
                        onChange={(value) => onValueChange(row.id, 'costPrice', value)}
                      />
                    </TableCell>
                    <TableCell>
                      <NumberField
                        ariaLabel={`${row.name} sell price`}
                        value={row.sellPrice}
                        onChange={(value) => onValueChange(row.id, 'sellPrice', value)}
                      />
                    </TableCell>
                    <TableCell>
                      <ReadOnlyField value={typeof row.margin === 'number' ? `${row.margin.toFixed(1)}%` : 'N/A'} />
                    </TableCell>
                    <TableCell>
                      <ChecklistStatusDot
                        approvalType={manuallyApproved ? 'manual' : 'auto'}
                        status={approved ? 'approved' : row.status}
                        band={approved ? 'green' : row.marginBand}
                      />
                    </TableCell>
                    <TableCell>
                      <ReadOnlyField value="N/A" />
                    </TableCell>
                    <TableCell>
                      <ReadOnlyField value="N/A" />
                    </TableCell>
                    <TableCell>
                      <ReadOnlyField value={row.collectedBy || 'N/A'} />
                    </TableCell>
                    <TableCell>
                      {!approved && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CheckIcon />}
                          onClick={() => onApprovalChange(row.id)}
                        >
                          Approve
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <HiddenApprovedFooter count={hiddenApprovedCount} noun="EGS row" showAutoApproved={showAutoApproved} />
    </ReportCard>
  );
}
